/**
 * Sentry Error Tracking & Performance Monitoring
 * Centralized configuration for error tracking, performance monitoring, and session replay
 */

import * as Sentry from '@sentry/react'

/**
 * Initialize Sentry for error tracking and performance monitoring
 * Should be called in main.tsx before rendering the app
 */
export function initSentry(): void {
  // Only initialize if DSN is configured (production environments)
  const dsn = import.meta.env.VITE_SENTRY_DSN
  if (!dsn) {
    return
  }

  Sentry.init({
    // Error Tracking
    dsn,
    environment: import.meta.env.MODE,
    enabled: import.meta.env.PROD, // Only enable in production builds
    release: import.meta.env.VITE_APP_VERSION ?? '1.0.0',

    // Performance Monitoring
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0, // 10% in prod, 100% in dev
    integrations: [
      Sentry.browserTracingIntegration(),
    ],
    tracePropagationTargets: ['localhost', /^\//],

    // Session Replay settings (disabled by default - configure in Sentry dashboard)
    replaysSessionSampleRate: 0, // Disabled - enable in Sentry dashboard
    replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors

    // Ignore certain errors
    ignoreErrors: [
      // Browser extensions
      'top.GLOBALS',
      'chrome-extension://',
      'moz-extension://',
      // Random plugins/extensions
      'Can\'t find variable: ZiteReader',
      'jigsaw is not defined',
      'ComboSearch is not defined',
      // Network errors that are expected
      'NetworkError',
      'Network request failed',
    ],

    // HTTP client integration settings
    sendDefaultPii: false,
    maxBreadcrumbs: 50,
    attachStacktrace: true,

    // Hook for filtering events
    beforeSend(event, hint) {
      // Filter out certain error types
      if (event.exception) {
        const error = hint.originalException
        if (error instanceof TypeError) {
          // You can filter specific errors here
          // return null // to discard the event
        }
      }

      return event
    },

    // Hook for filtering breadcrumbs
    beforeBreadcrumb(breadcrumb) {
      // Filter out certain breadcrumbs (console logs, XHR requests to specific domains, etc.)
      if (breadcrumb.category === 'console') {
        // Filter debug console logs
        if (breadcrumb.level === 'debug') {
          return null
        }
      }

      // Ignore certain XHR requests
      if (breadcrumb.category === 'fetch' || breadcrumb.category === 'xhr') {
        if (breadcrumb.data?.url?.includes('/api/health')) {
          return null
        }
      }

      return breadcrumb
    },
  })
}

/**
 * Note: Sentry advanced features like React Router integration and Session Replay
 * can be configured in the Sentry dashboard. This module provides core error tracking
 * and performance monitoring capabilities.
 */

/**
 * Manually capture an exception
 */
export function captureException(error: Error, context?: Record<string, unknown>): void {
  if (context) {
    Sentry.captureException(error, {
      contexts: {
        app: context as Record<string, unknown>,
      },
    })
  } else {
    Sentry.captureException(error)
  }
}

/**
 * Manually capture a message
 */
export function captureMessage(
  message: string,
  level: 'fatal' | 'error' | 'warning' | 'info' | 'debug' = 'info'
): void {
  Sentry.captureMessage(message, level)
}

/**
 * Set user context for error tracking
 * Call this after user login
 */
export function setSentryUser(userId: string, email?: string, username?: string): void {
  Sentry.setUser({
    id: userId,
    email,
    username,
  })
}

/**
 * Clear user context
 * Call this on logout
 */
export function clearSentryUser(): void {
  Sentry.setUser(null)
}

/**
 * Add custom breadcrumb
 */
export function addBreadcrumb(
  message: string,
  category?: string,
  level?: 'fatal' | 'error' | 'warning' | 'info' | 'debug'
): void {
  Sentry.addBreadcrumb({
    message,
    category: category || 'custom',
    level: level || 'info',
    timestamp: Date.now() / 1000,
  })
}

/**
 * Higher-order function to wrap async functions with Sentry error tracking
 * Automatically captures and re-throws any errors that occur
 */
export function withSentry<Args extends any[], R>(
  fn: (...args: Args) => Promise<R>,
  options?: { name?: string }
) {
  return async (...args: Args): Promise<R> => {
    try {
      return await fn(...args)
    } catch (err: unknown) {
      const fnName = options?.name || fn.name || 'async_function'
      Sentry.captureException(err, {
        contexts: {
          function: { name: fnName },
        },
      })
      throw err
    }
  }
}
